/* eslint-disable @next/next/no-img-element */
import { useLazyQuery, useQuery } from '@apollo/client';
import classNames from 'classnames';
import { get, isEmpty } from 'lodash';
import Link from 'next/link';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Container from '~/components/Container';
import Icon from '~/components/Icon';
import SectionTitle from '~/components/SectionTitle';
import Text from '~/components/Text';
import Colors from '~/constants/colors';
import { Identifiers, ProductListKey, RelatedPosition } from '~/constants/enum';
import { QUERY_GET_CMS_BLOCKS, QUERY_OPTIMISTIC_TRENDING } from '~/data/home';
import { Product } from '~/services/product';
import { transformProductItems } from '~/utils/transform';
import ProductsSlider from '../../components/ProductsSlider';
import styles from './Trending.module.scss';

import 'swiper/css/grid';
import { Grid } from 'swiper/modules';
import useProductRelatedBySkus from '~/hooks/useProductRelatedBySkus';
import { useRecoilValue } from 'recoil';
import storeConfigAtom from '~/services/store-config/store-config.atom';
import {
    QUERY_TRACK_VIEW_COLLECTION,
    QUERY_TRACK_VIEW_ITEM_RELATED,
    QUERY_TRACKING_IMPRESSION,
    QUERY_TRACKING_VIEW_RELATED
} from '~/data/product/gql';

interface TrendingProps {
    title?: string;
    image?: string;
    directLabel?: string;
    directUrl?: string;
    numberOfItems?: number;
    skip?: boolean;
    data?: Product[];
}

const Trending: FC<TrendingProps> = ({
    title = 'TRENDING',
    image,
    directLabel,
    directUrl,
    // directLabel = "See All",
    // directUrl = "#",
    data,
    numberOfItems = 3.5,
    skip
}) => {
    const { trending_group_id } = useRecoilValue(storeConfigAtom?.store);

    const [startFetching, setStartFetching] = useState(false);
    const [isIntersecting, setIsIntersecting] = useState(false);

    const [queryTrackViewItemRelated] = useLazyQuery(QUERY_TRACK_VIEW_ITEM_RELATED);

    const [queryTrackingViewList] = useLazyQuery(QUERY_TRACKING_VIEW_RELATED);
    const [queryTrackingViewCollection] = useLazyQuery(QUERY_TRACK_VIEW_COLLECTION);

    const [relatedTrackingImpression] = useLazyQuery(QUERY_TRACKING_IMPRESSION);

    const fetchBanner = useQuery(QUERY_GET_CMS_BLOCKS, {
        variables: {
            identifiers: [Identifiers.TrendingBanner]
        },
        context: { fetchOptions: { method: 'GET' } }
    });

    const { products, loading, relatedProductsBySkus } = useProductRelatedBySkus({
        ruleId: trending_group_id,
        limit: 12,
        position: RelatedPosition.HomePage,
        skip: !startFetching
    });

    const _image: string = useMemo(() => {
        if (!!skip) {
            return image;
        }
        if (isEmpty(fetchBanner?.data?.cmsBlocks?.items?.[0])) return {};

        const cmsBanner = get(fetchBanner?.data?.cmsBlocks?.items?.[0], 'content', {});
        const img = cmsBanner?.match(/src="(.*?)"/)?.[1] || '';
        return img;
    }, [fetchBanner?.data?.cmsBlocks, image, skip]);

    const handleStartFetching = (entries: IntersectionObserverEntry) => {
        setStartFetching(true);
    };
    const handleImpression = (products: any[]) => {
        console.log('handleImpression', trending_group_id, products);
        queryTrackViewItemRelated({
            variables: {
                block_id: trending_group_id,
                skus: products?.map((prod) => prod?.sku)
            }
        });
        relatedTrackingImpression({
            variables: {
                skus: products?.map((prod) => prod?.sku),
                block_id: trending_group_id,
                type: 'related',
                search_query: ''
            }
        });
    };

    const handleOnEnter = () => {
        console.log('queryTrackingViewList', trending_group_id);
        setIsIntersecting(true);
    };
    useEffect(() => {
        if (isIntersecting) {
            queryTrackingViewList({
                variables: {
                    block_id: trending_group_id
                }
            });
            queryTrackingViewCollection({
                variables: {
                    block_id: trending_group_id,
                    type: 'related'
                }
            });
        }
    }, [isIntersecting, trending_group_id]);

    // if (!loading && isEmpty(products)) return null;

    const _numberOfItems = !!isEmpty(_image) ? 5.5 : numberOfItems;

    // if (fetchBanner.loading || loading) return null;

    if (!products?.length && !loading && !!startFetching) {
        return null;
    }

    return (
        <Container rootClassName={styles.trending_wrapper} className={styles.trending}>
            <SectionTitle
                text={relatedProductsBySkus?.block_title ?? title}
                action={
                    directUrl ? (
                        <Link href={directUrl} className="flex items-center gap-1 text-primary">
                            <Text type="title-1">{directLabel}</Text>
                            <Icon name="arrow-up-right" />
                        </Link>
                    ) : undefined
                }
            />
            <div className="flex flex-col gap-8 md:flex-row lg:gap-10">
                {!isEmpty(_image) && (
                    <div className={classNames(styles.banner, 'hidden lg:block')}>
                        <img className="object-cover" src={_image} alt={title || 'Image'} height="100%" width="100%" />
                    </div>
                )}
                <div className="flex-1 overflow-hidden">
                    <ProductsSlider
                        listkey={trending_group_id}
                        products={products}
                        slidesPerView={_numberOfItems}
                        grid={{
                            rows: 1
                        }}
                        modules={[Grid]}
                        loading={loading}
                        // onLoadingMore={() => handleFecthMore(_pageInfo?.current_page + 1)}
                        onBeforeIntersection={handleStartFetching}
                        onEnter={handleOnEnter}
                        onImpressions={handleImpression}
                        hasNext={false}
                        breakpoints={{
                            1024: {
                                slidesPerView: _numberOfItems,
                                grid: {
                                    rows: 2,
                                    fill: 'row'
                                }
                            },
                            768: {
                                slidesPerView: _numberOfItems - 2
                            }
                        }}
                    />
                </div>
            </div>
        </Container>
    );
};

export default Trending;
